// src-pwa/main.js
import { initializeApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getAuth, signInWithCustomToken, onAuthStateChanged } from "firebase/auth";

// Debug flag to control logging
const debug_mode = false;

const isCapacitor = typeof window !== 'undefined' && !!window.Capacitor;
const isCapacitorApp = () => {
  return typeof window !== 'undefined' && window.Capacitor && ['android', 'ios'].includes(window.Capacitor.getPlatform());
};

// Define plugin variables that can be accessed globally
let PushNotifications, App, StatusBar;

// Wait for Capacitor to be ready before accessing plugins
const initializeCapacitorPlugins = async () => {
  if (isCapacitorApp()) {
    try {
      // Wait for Capacitor to be fully ready
      await window.Capacitor.Plugins;
      
      // Now safely access the plugins
      PushNotifications = window.Capacitor.Plugins.PushNotifications;
      App = window.Capacitor.Plugins.App;
      StatusBar = window.Capacitor.Plugins.StatusBar;
      //Browser = window.Capacitor.Plugins.Browser;
      
      if (debug_mode) console.log('✅ Capacitor plugins initialized:', {
        PushNotifications: !!PushNotifications,
        App: !!App,
        StatusBar: !!StatusBar,
        //Browser: !!Browser
      });
      
      return true;
    } catch (error) {
      console.error('❌ Error initializing Capacitor plugins:', error);
      return false;
    }
  }
  return false;
};

// Initialize plugins immediately when script loads
let capacitorReady = false;
(async () => {
  capacitorReady = await initializeCapacitorPlugins();
})();


// Status bar spacing function
const applyStatusBarSpacing = async () => {
  if (debug_mode) console.log('🔍 applyStatusBarSpacing called');
  if (debug_mode) console.log('🔍 isCapacitorApp():', isCapacitorApp());
  
  if (!isCapacitorApp() || !StatusBar) {
    if (debug_mode) console.log('🔍 Skipping status bar spacing - not Capacitor app or StatusBar not available');
    return;
  }
  
  const platform = Capacitor.getPlatform();
  if (debug_mode) console.log('🔍 Platform detected:', platform);
  
  try {
    if (debug_mode) console.log('🔍 StatusBar plugin:', !!StatusBar);
    
    if (platform === 'ios') {
      // iOS: Use CSS safe areas instead of manual padding
      if (debug_mode) console.log('🍎 Configuring iOS status bar with safe areas');
      
      // Set status bar style for iOS
      await StatusBar.setStyle({ style: 'LIGHT' });
      
      // Use CSS safe area instead of manual padding
      document.documentElement.style.setProperty('--status-bar-height', 'env(safe-area-inset-top)');

      // Ensure viewport meta tag is set correctly
      // <meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=no">
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0, user-scalable=no');
      } else {
        const newViewportMeta = document.createElement('meta');
        newViewportMeta.name = 'viewport';
        newViewportMeta.content = 'width=device-width, initial-scale=1.0, user-scalable=no';
        document.head.appendChild(newViewportMeta);
      }

      // Remove any existing padding that might conflict
      document.body.style.paddingTop = '0px';
      document.body.style.backgroundColor = ''; // Remove the red debug background
      
      if (debug_mode) console.log('✅ iOS status bar configured with safe area insets');
      
    } else if (platform === 'android') {
      // Android: Use manual spacing as before
      if (debug_mode) console.log('🤖 Configuring Android status bar with manual spacing');
      
      // Force overlay off (Android only)
      await StatusBar.setOverlaysWebView({ overlay: false });
      await StatusBar.setStyle({ style: 'DARK' });
      await StatusBar.setBackgroundColor({ color: '#ffffff' });
      
      // Wait a moment for the setting to take effect
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Get the actual status bar height
      let statusBarHeight = 24; // Default for Android
      
      try {
        const info = await StatusBar.getInfo();
        if (debug_mode) console.log('🔍 StatusBar.getInfo():', info);
        statusBarHeight = info.height || 24;
      } catch (error) {
        if (debug_mode) console.log('🔍 Could not get status bar height, using Android default:', error);
      }
      
      if (debug_mode) console.log('🔍 Final statusBarHeight:', statusBarHeight);
      
      if (statusBarHeight > 0) {
        document.body.style.backgroundColor = ''; // Remove debug background
        document.documentElement.style.setProperty('--status-bar-height', `${statusBarHeight}px`);
        
        if (debug_mode) console.log(`✅ Applied ${statusBarHeight}px top padding for Android status bar`);
      } else {
        if (debug_mode) console.log('❌ No status bar height determined');
      }
    }
    
  } catch (error) {
    console.error('❌ Error applying status bar spacing:', error);
  }
};
const configureStatusBar = async () => {
  if (!isCapacitorApp() || !StatusBar) {
    if (debug_mode) console.log('🔍 Skipping status bar config - not Capacitor app or StatusBar not available');
    return;
  }
  
  const platform = Capacitor.getPlatform();
  if (debug_mode) console.log('🔍 Configuring status bar for platform:', platform);
  
  try {
    if (platform === 'ios') {
      // iOS configuration
      await StatusBar.setStyle({ style: 'LIGHT' });
      // Note: iOS doesn't support setBackgroundColor or setOverlaysWebView
      if (debug_mode) console.log('✅ iOS status bar style configured');
      
    } else if (platform === 'android') {
      // Android configuration
      await StatusBar.setStyle({ style: 'DARK' });
      await StatusBar.setBackgroundColor({ color: '#ffffff' });
      await StatusBar.setOverlaysWebView({ overlay: false });
      if (debug_mode) console.log('✅ Android status bar configured');
    }
    
  } catch (error) {
    console.error('❌ Error configuring status bar:', error);
  }
};

// --- FIREBASE INITIALIZATION ---
// Firebase config for different platforms
// Key not needed due to remote loading?
// ANDROID KEY: AIzaSyDeVpkD824pMq9Yad48c-2WTye-rsn8f2E ???
// IOS KEY: AIzaSyBMvhnMma80nwZAJ3YcsVeF9lGycGQHZBs
// BROWSER KEY: AIzaSyCjE4KslsKDNrGrM1q6gLUHSher7dks_AE


const androidFirebaseConfig = {
  appId: '1:700755700570:android:4ce835d2a4e3e06bf76163',
};

const iosFirebaseConfig = {
  appId: '1:700755700570:ios:55480c12ed72d298f76163',
};

let APPID = '';
if (isCapacitorApp()) {
  const platform = Capacitor.getPlatform();
  if (debug_mode) console.log('Initializing Firebase for Capacitor platform:', platform);

  if (platform === 'android') {
    APPID = androidFirebaseConfig.appId;
  } else if (platform === 'ios') {
    APPID = iosFirebaseConfig.appId;
  }
} else {
  if (debug_mode) console.log('Initializing Firebase for web platform');
  APPID = '1:700755700570:web:352fb93c8feb951bf76163'; // Web app ID
}

// Initialize Firebase using correct config based on platform
const firebaseConfig = {
  apiKey: 'AIzaSyCjE4KslsKDNrGrM1q6gLUHSher7dks_AE',
  authDomain: 'pm.arconbuildings.com',
  projectId: 'arconpm-3d536',
  storageBucket: 'arconpm-3d536.firebasestorage.app',
  messagingSenderId: '700755700570',
  appId: APPID // Set above based on platform
};

const app = initializeApp(firebaseConfig);
const functions = getFunctions(app);
const auth = getAuth(app);

// --- Define the callable function references ---
const saveFCMToken = httpsCallable(functions, 'saveFCMToken');
const sendPushNotification = httpsCallable(functions, 'sendPushNotification');
// --- SIMPLE TEST FOR sendPushNotification FUNCTION ---
// This test will attempt to send a push notification to a test user (replace with real userId and valid data as needed)
async function testSendPushNotification() {
  try {
    const response = await sendPushNotification({
      recipientId: 'SD74U7ke8XPzHFMwVrg8NCj6YDk1', // Replace with a real user ID in your Firestore
      title: 'Test Notification',
      body: 'This is a test push notification from main.js!',
      data: { customKey: 'customValue' }
    });
    if (debug_mode) console.log('sendPushNotification response:', response.data);
  } catch (error) {
    console.error('sendPushNotification error:', error);
  }
}

// Uncomment the line below to run the test on page load (for dev only)
// testSendPushNotification();


        // Function to get custom token from PHP session
        async function getCustomTokenFromServer() {
          try {
            const response = await fetch('/_app/get-firebase-token.php', {
              method: 'GET',
              credentials: 'include' // Include session cookies
            });
            
            if (!response.ok) {
              throw new Error('Failed to get custom token');
            }
            
            const data = await response.json();
            return data.customToken;
          } catch (error) {
            console.error('Error getting custom token:', error);
            return null;
          }
        }


// Listen for auth state changes and then register push notifications
onAuthStateChanged(auth, (user) => {
  if (user) {
    if (debug_mode) console.log('User is signed in:', user.uid);

    // Display current user ID on page for easy copying
    const userIdDisplay = document.getElementById('current-user-id');
    if (userIdDisplay) {
      userIdDisplay.textContent = `Current User ID: ${user.uid}`;
    }

    if (isCapacitorApp()) {
      // Now that we have a user, register for push notifications
      registerPushNotifications();
    }

  } else {
    if (debug_mode) console.log('No user signed in. Attempting anonymous sign-in...');
        authenticateWithCustomToken();
  }
});

        // New function to handle custom token authentication
        async function authenticateWithCustomToken() {
          try {
            const customToken = await getCustomTokenFromServer();
            
            if (!customToken) {
              console.error('No custom token available - user may not be logged in to PHP session');
              return;
            }
            
            const userCredential = await signInWithCustomToken(auth, customToken);
            if (debug_mode) console.log('Successfully signed in with custom token:', userCredential.user.uid);
            
          } catch (error) {
            console.error('Custom token sign-in failed:', error);
          }
        }

// --- CAPACITOR PUSH NOTIFICATIONS HANDLING ---
const registerPushNotifications = async () => {
  if (!PushNotifications) {
    console.error('❌ PushNotifications plugin not available');
    return;
  }
  
  try {
    let permStatus = await PushNotifications.requestPermissions();

    if (permStatus.receive === 'granted') {
      await PushNotifications.register(); // Register with native FCM
      if (debug_mode) console.log('Push Notifications registered successfully.');
    } else {
      console.warn('Push Notifications permission denied.');
      return; // Exit if permission denied
    }

    PushNotifications.addListener('registration', async (token) => {
      if (debug_mode) console.log('FCM Registration token from Capacitor:', token.value);

      // --- FINAL FIX: Use the Firebase SDK's `httpsCallable` ---
      // This is the correct way to call an `onCall` function. The SDK automatically
      // handles attaching the user's authentication token, which fixes the
      // "must be called while authenticated" error.
      try {
        if (debug_mode) console.log("Calling 'saveFCMToken' via httpsCallable...");
        const result = await saveFCMToken({ fcmToken: token.value });
        if (debug_mode) console.log("SUCCESS: Cloud function responded:", result.data);
      } catch (error) {
        console.error("ERROR calling cloud function via httpsCallable:", 
                      "Code:", error.code, 
                      "Message:", error.message, 
                      "Details:", error.details, 
                      "Full Error Object:", error); // Also log the full object just in case
      }
    });

    // Event listener for registration errors
    PushNotifications.addListener('registrationError', (error) => {
      console.error('Capacitor Push Notifications registration error:', error);
    });

    // Event listener for when a push notification is received
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      if (debug_mode) console.log('Push notification received:', notification);
    });

    // Event listener for when a user taps on a push notification
    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      if (debug_mode) console.log('Push notification action performed:', notification);
    });

  } catch (error) {
    console.error('General error setting up Capacitor Push Notifications:', error);
  }
};


// Add this new function for user-to-user notifications
/* async function sendUserToUserNotification(options = null) { */
window.sendUserToUserNotification = async function sendUserToUserNotification(options = null) {

  if (options == null) {options = {};}
  const defaultOptions = {
    recipientId: '', // User ID of the recipient
    title: 'Hello from ArcProject!',
    body: 'This is a user-to-user notification.',
    data: { custom: 'value' }
  };
  // What does the '...' mean?
  // It is the spread operator, which allows us to merge objects.
  // Here, we are merging the default options with any options passed in.
  // If an option is not provided, the default will be used.
  options = { ...defaultOptions, ...options };

  // Validate inputs
  const recipientId = options.recipientId;
  const title = options.title.trim();
  const body = options.body.trim();
  const data = options.data;

  if (debug_mode) console.log('Sending user-to-user notification from Capacitor app...');
  try {
    const response = await sendPushNotification({
      recipientId: recipientId,
      title: title,
      body: body,
      data: {
        type: 'user-message',
        timestamp: Date.now().toString()
      }
    });
    if (debug_mode) console.log('User-to-user notification sent:', response.data);
  } catch (error) {
    console.error('Error sending user-to-user notification:', error);
  }

}


document.addEventListener('DOMContentLoaded', async () => {
  if (debug_mode) console.log('🚀 DOM loaded - setting up listeners...');

  if (isCapacitorApp()) { 
    const platform = Capacitor.getPlatform();
    document.body.classList.add('mobile-app');
    document.body.classList.add(platform); // Add platform-specific class

    if (capacitorReady) {
      // Apply status bar spacing first
      await applyStatusBarSpacing();

      // Then configure other aspects
      await configureStatusBar();

      // Interceptor for PDF links to open in Capacitor Browser
      if (debug_mode) console.log('SETTING UP PDF LINK INTERCEPTOR FOR CAPACITOR BROWSER');
      if (typeof window !== 'undefined' && !window.__pdfClickHandlerAttached__) {
        window.__pdfClickHandlerAttached__ = true;

        if (debug_mode) console.log('Setting up listener one time!');

        document.addEventListener('click', async (e) => {
          const el = e.target.closest('a');
          if (!el) return;

          const href = el.getAttribute('href');
          const encodedUrl = encodeURI(href); // use encodeURI, not encodeURIComponent
          const absoluteUrl = new URL(encodedUrl, window.location.origin).href;

          if (debug_mode) console.log('Encoded relative link:', encodedUrl);
          if (debug_mode) console.log('Absolute URL:', absoluteUrl);

          const isPdf = href && href.endsWith('.pdf');

          if (isPdf && window.Capacitor && window.Capacitor.Plugins?.Browser) {
            e.preventDefault();

            try {
              await window.Capacitor.Plugins.Browser.open({ url: absoluteUrl });
            } catch (err) {
              console.error('[Capacitor] Failed to open PDF in browser:', err);
            }
          }
        });
      }

    }
  }

  // FIXME: Reload Disabled for now to prevent interuppting uploads
  // Try multiple approaches for app state detection
  // setupAppStateListener();      // Capacitor App plugin
  // setupVisibilityListener();    // Document visibility API
  // setupFocusListener();         // Window focus events

  // Add handler for user-to-user notifications
  const sendNotificationBtn = document.getElementById('send-notification-btn');
  if (sendNotificationBtn) {
    sendNotificationBtn.addEventListener('click', () => {
      let user_id_input = document.getElementById('recipient-select').value;

      const options = {
        recipientId: user_id_input,
        title: 'You\'ve Been Tagged!',
        body: 'Please review your alerts in the app dashboard.',
        data: { custom: 'value' }
      };

      sendUserToUserNotification(options);
    });
  }
});

// Add this function to handle app state changes
const setupAppStateListener = () => {
  if (!isCapacitorApp() || !App) {
    if (debug_mode) console.log('Not a Capacitor app or App plugin not available - skipping app state listeners');
    return;
  }
  
  if (debug_mode) console.log('Setting up app state listeners for Capacitor...');
  
  try {
    // Test if the App plugin is available
    if (debug_mode) console.log('App plugin available:', !!App);
    
    // Listen for when the app becomes active (foreground)
    App.addListener('appStateChange', (state) => {
      if (debug_mode) console.log('🔄 App state changed:', state);
      if (debug_mode) console.log('🔄 Is active:', state.isActive);

      if (state.isActive) {
        if (debug_mode) console.log('✅ App came to foreground - refreshing page...');
        window.location.reload();
      }
    });
    
    // Listen for when app is resumed
    App.addListener('resume', () => {
      if (debug_mode) console.log('🔄 App resumed from background - refreshing page...');
      window.location.reload();
    });
    
    // Listen for when app goes to background
    App.addListener('pause', () => {
      if (debug_mode) console.log('🔄 App went to background');
    });
    
    if (debug_mode) console.log('✅ App state listeners registered successfully');
    
  } catch (error) {
    console.error('❌ Error setting up app state listeners:', error);
  }
};

// Alternative approach using document visibility API (works in both web and Capacitor)
const setupVisibilityListener = () => {
  if (debug_mode) console.log('Setting up document visibility listener...');
  
  document.addEventListener('visibilitychange', () => {
    if (debug_mode) console.log('📄 Document visibility changed:', document.visibilityState);
    
    if (document.visibilityState === 'visible') {
      if (debug_mode) console.log('📄 Document became visible');
      
      if (isCapacitorApp()) {
        if (debug_mode) console.log('📱 Capacitor app became visible - refreshing...');
        window.location.reload();
      }
    } else {
      if (debug_mode) console.log('📄 Document became hidden');
    }
  });
};

// Alternative approach using page focus events
const setupFocusListener = () => {
  if (debug_mode) console.log('Setting up window focus listener...');
  
  window.addEventListener('focus', () => {
    if (debug_mode) console.log('🔍 Window gained focus');
    
    if (isCapacitorApp()) {
      if (debug_mode) console.log('📱 Capacitor app gained focus - refreshing...');
      window.location.reload();
    }
  });
  
  window.addEventListener('blur', () => {
    if (debug_mode) console.log('🔍 Window lost focus');
  });
};

if (debug_mode) console.log('Finished with main.js setup.');